import React from 'react';

import {Card, List, ListItem, Button, Col, Row, Icon, Label, NoData, Text, Space, showConfirm, ButtonGroup, Modal} from '@mc/ui-v4';

import {mcNew, mcDelete} from '@mc/react-ui-icons';
import {RightsApi, PracovnikEosVO, TopicVO} from '../../../api';
import {CreateTopicOwnerModal, SingleOpravneniProps} from './CreateTopicOwnerModal';
import {useQuery, QueryClient, useQueryClient} from 'react-query';
import {CACHE_KEY_TOPICS_OWNED, CACHE_KEY_TOPICS_OWNERS} from '../../../queryKeys';
import {printFormatterUredniOsobaNazevEOS} from '../../../helpers/Formatter';
import {actionApiCall, rffFormSubmit, WaitForQueryData} from '@mc/react-app-v4';
import './OpravneniConfiguration.scss';
import classNames from 'classnames';
import {useAuth} from '@mc/react-auth';

type TopicOwnersProps = {
    topic: TopicVO;
};
export const TopicOwnersConfiguration: React.FC<TopicOwnersProps> = ({topic}) => {
    const queryClient: QueryClient = useQueryClient();
    const {data: auth} = useAuth();
    const useTopicsOwnersQuery = useQuery<PracovnikEosVO[]>([CACHE_KEY_TOPICS_OWNERS, topic.id], async () => (await RightsApi.getTopicOwners(topic.id)).data);

    const handleCreateOpravneni = () => {
        Modal.show(({onClose}) => (
            <CreateTopicOwnerModal
                onClose={onClose}
                topicLabel={topic.label}
                onSubmit={async data => {
                    console.log(data);
                    return rffFormSubmit(async () => {
                        await RightsApi.addTopicOwners(topic.id, getIdPracovnikuFromData(data));
                        await queryClient.invalidateQueries([CACHE_KEY_TOPICS_OWNERS, topic.id]);
                        await queryClient.invalidateQueries([CACHE_KEY_TOPICS_OWNED]);
                        onClose();
                    });
                }}
            />
        ));
    };

    const getIdPracovnikuFromData = (data: SingleOpravneniProps): number[] => {
        switch (data.entita.type) {
            case 'PRACOVNIK':
                return [data.entita.pracovnik.pracovnikId];
            case 'ORG_JEDNOTKA':
                return data.entita.organizacniJednotka.pracovnici.map(p => p.pracovnikId);
            case 'ORG_ROLE':
                return data.entita.organizacniRole.pracovnici.map(p => p.pracovnikId);
        }
    };

    const handleRemovePrava = async (pracovnik: PracovnikEosVO) => {
        const result = await showConfirm({
            title: 'Odstranit vlastníka',
            message: `Opravdu chcete odstranit vlastníka ${printFormatterUredniOsobaNazevEOS(pracovnik)}?`,
            intent: 'danger',
        });
        if (result) {
            await actionApiCall(() => RightsApi.removeTopicOwner(topic.id, pracovnik.pracovnikId));
            await queryClient.invalidateQueries([CACHE_KEY_TOPICS_OWNED]);
            await queryClient.invalidateQueries([CACHE_KEY_TOPICS_OWNERS, topic.id]);
        }
    };

    return (
        <WaitForQueryData queries={[useTopicsOwnersQuery]}>
            {() => {
                const users = useTopicsOwnersQuery.data;
                const isUserVlastnikOrAdmin: boolean = auth?.admin || users?.some(user => user.pracovnikId === auth?.pracovnikId);

                return (
                    <Card
                        mode={'view'}
                        title={'Vlastníci tématu'}
                        extra={isUserVlastnikOrAdmin && <Button style={'link'} onClick={() => handleCreateOpravneni()} icon={<Icon icon={mcNew} />} />}
                    >
                        <List mode={'labels'}>
                            <ListItem>
                                <Row className={'py-0'}>
                                    <Col xs={12}>
                                        <Label>Uživatel</Label>
                                    </Col>
                                </Row>
                            </ListItem>
                        </List>
                        <List>
                            {!users || users?.length === 0 ? (
                                <NoData />
                            ) : (
                                <>
                                    {users.map((user: PracovnikEosVO, index: number) => {
                                        return (
                                            <ListItem
                                                className={classNames({['custom-rounded-bottom']: index === users.length - 1})}
                                                key={`${index}-${user.pracovnikId}`}
                                                extra={
                                                    <ButtonGroup className={'float-right'}>
                                                        {isUserVlastnikOrAdmin && (
                                                            <Button
                                                                icon={<Icon icon={mcDelete} />}
                                                                style={'link'}
                                                                key={`3-dots-${index}-${user.pracovnikId}`}
                                                                showOnHover
                                                                title={'Smazat vlastníka tématu'}
                                                                onClick={() => handleRemovePrava(user)}
                                                            />
                                                        )}
                                                    </ButtonGroup>
                                                }
                                            >
                                                <Row className={'py-0'}>
                                                    <Col xs={12} className={'d-flex flex-column negative-margin'}>
                                                        <Space>
                                                            <Text strong>{printFormatterUredniOsobaNazevEOS(user)}</Text>
                                                        </Space>
                                                        <Text intent={'secondary'}>{user.email}</Text>
                                                    </Col>
                                                </Row>
                                            </ListItem>
                                        );
                                    })}
                                </>
                            )}
                        </List>
                    </Card>
                );
            }}
        </WaitForQueryData>
    );
};
